<script lang="ts">
  import { onMount } from 'svelte';
  import type { Component } from 'svelte';
  import type { Snippet } from 'svelte';
  import { getAppContext } from './context.js';
  import ErrorBoundary from './ErrorBoundary.svelte';
  import { resolveRouteGuards } from './guards.js';
  import { loadRouteComponent } from './routes.js';
  import { titleForRoute } from './route-meta.js';
  import type { RouteLocation } from './router.js';

  interface Props {
    children?: Snippet;
  }

  let { children }: Props = $props();

  const ctx = getAppContext();
  const { router, toast } = ctx;

  let location = $state<RouteLocation>(router.location);
  let PageComponent = $state<Component | null>(null);
  let loading = $state(true);
  let loadToken = 0;

  async function renderRoute(next: RouteLocation) {
    const token = ++loadToken;
    loading = true;

    const guard = await resolveRouteGuards(next, ctx.repositories.matches);
    if (token !== loadToken) return;

    if (guard.location.name !== next.name || guard.location.path !== next.path) {
      if (guard.flash) {
        router.replace(guard.location.name, guard.location.params as never, { flash: guard.flash });
      } else {
        router.replace(guard.location.name, guard.location.params as never);
      }
      return;
    }

    location = guard.location;
    document.title = titleForRoute(location.name);

    const component = await loadRouteComponent(location.name);
    if (token !== loadToken) return;

    PageComponent = component;
    loading = false;
  }

  onMount(() => {
    const flash = router.consumeFlash();
    if (flash) {
      toast.show(flash);
    }

    void renderRoute(router.location);

    return router.subscribe((next) => {
      const incomingFlash = router.consumeFlash();
      if (incomingFlash) {
        toast.show(incomingFlash);
      }
      void renderRoute(next);
    });
  });

  const isGameRoute = $derived(
    location.name === 'game' || location.name === 'replay',
  );
</script>

<div
  class="tx42-app-root min-h-[100dvh] bg-tx42-felt-deep"
  data-route={location.name}
  data-shell={isGameRoute ? 'game' : 'standard'}
>
  {#if children}
    {@render children()}
  {:else if loading}
    <div
      class="flex min-h-[100dvh] items-center justify-center px-4 text-white/80"
      role="status"
      aria-live="polite"
      data-testid="route-loading"
    >
      Loading…
    </div>
  {:else if PageComponent}
    {#key `${location.name}:${location.path}`}
      <ErrorBoundary>
        <PageComponent />
      </ErrorBoundary>
    {/key}
  {/if}
</div>
